import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useUsers = (isAdmin) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    if (!isAdmin || !db) {
      setUsers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const usersRef = collection(db, 'artifacts/bz-tracker/users');
      // Don't use orderBy to include users without lastLoginAt field
      const usersSnapshot = await getDocs(usersRef);

      const usersData = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          const userId = userDoc.id;

          // Count tracked properties
          let propertyCount = 0;
          try {
            const propsRef = collection(db, `artifacts/bz-tracker/users/${userId}/trackedProperties`);
            const propsSnapshot = await getDocs(propsRef);
            propertyCount = propsSnapshot.size;
          } catch (e) {
            // Subcollection might not exist
          }

          // Count tracked searches
          let searchCount = 0;
          try {
            const searchesRef = collection(db, `artifacts/bz-tracker/users/${userId}/trackedSearches`);
            const searchesSnapshot = await getDocs(searchesRef);
            searchCount = searchesSnapshot.size;
          } catch (e) {
            // Subcollection might not exist
          }

          return {
            id: userId,
            email: userData.email || '',
            displayName: userData.displayName || 'Anonymous User',
            isAdmin: userData.isAdmin || false,
            role: userData.role || 'user',
            createdAt: userData.createdAt?.toDate?.() || null,
            lastLoginAt: userData.lastLoginAt?.toDate?.() || null,
            ai: userData.ai || {},
            propertyCount,
            searchCount
          };
        })
      );

      // Sort by lastLoginAt descending (users without lastLoginAt go to end)
      usersData.sort((a, b) => {
        if (!a.lastLoginAt && !b.lastLoginAt) return 0;
        if (!a.lastLoginAt) return 1;
        if (!b.lastLoginAt) return -1;
        return b.lastLoginAt - a.lastLoginAt;
      });

      setUsers(usersData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleAdmin = async (userId, currentIsAdmin) => {
    if (!db) return;

    try {
      const userRef = doc(db, `artifacts/bz-tracker/users/${userId}`);
      await updateDoc(userRef, {
        isAdmin: !currentIsAdmin,
        role: !currentIsAdmin ? 'admin' : 'user'
      });

      // Update local state
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, isAdmin: !currentIsAdmin, role: !currentIsAdmin ? 'admin' : 'user' }
          : user
      ));

      return true;
    } catch (err) {
      console.error('Error toggling admin status:', err);
      throw err;
    }
  };

  const refreshUsers = () => {
    fetchUsers();
  };

  // Admin-only: update a user's AI policy/status-mirror fields. Never touches
  // key material (keys live in the deny-all `private/ai` subdoc).
  const updateUserAI = async (userId, partial) => {
    if (!db) return;
    const allowed = ['provider', 'model', 'byokDisabled', 'useHouseKey', 'lockedByAdmin'];
    const updates = {};
    for (const [k, v] of Object.entries(partial)) {
      if (allowed.includes(k)) updates[`ai.${k}`] = v;
    }
    if (Object.keys(updates).length === 0) return;

    const userRef = doc(db, `artifacts/bz-tracker/users/${userId}`);
    await updateDoc(userRef, updates);

    setUsers((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, ai: { ...(user.ai || {}), ...partial } } : user
      )
    );
  };

  return {
    users,
    isLoading,
    error,
    toggleAdmin,
    updateUserAI,
    refreshUsers
  };
};
